import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { UploadCloud, FileSpreadsheet, Sparkles, Check, X, ChevronDown, Trash2, AlertTriangle, RotateCcw } from "lucide-react";
import {
  menuUpload, menuListBatches, menuGetBatch, menuCanonicalize, menuImport, menuDeleteBatch,
  menuCapabilities,
} from "../../lib/adminApi";

const CATEGORIES = [
  "gin", "whisky", "rum", "tequila", "mezcal", "vodka", "liqueurs",
  "agave", "aperitifs", "wine", "beer", "sake", "vermouth", "amaro",
];

export default function AdminMenuUpload() {
  const [batches, setBatches] = useState([]);
  const [activeBatch, setActiveBatch] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [caps, setCaps] = useState(null);
  const inputRef = useRef(null);

  const loadBatches = async () => {
    try {
      const r = await menuListBatches();
      setBatches(r.batches || []);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load batches");
    }
  };

  useEffect(() => {
    loadBatches();
    menuCapabilities().then(setCaps).catch(() => setCaps(null));
  }, []);

  const openBatch = async (id) => {
    try {
      const r = await menuGetBatch(id);
      setActiveBatch(r);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load batch");
    }
  };

  const onUpload = async (file) => {
    if (!file) return;
    setBusy(true); setError(null);
    try {
      const r = await menuUpload(file);
      await loadBatches();
      await openBatch(r.batch_id);
    } catch (e) {
      setError(e.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const onCanonicalize = async () => {
    if (!activeBatch) return;
    setBusy(true); setError(null);
    try {
      await menuCanonicalize(activeBatch.id);
      await openBatch(activeBatch.id);
      await loadBatches();
    } catch (e) {
      setError(e.response?.data?.detail || "Canonicalize failed");
    } finally { setBusy(false); }
  };

  const onDeleteBatch = async (id) => {
    if (!window.confirm("Delete this batch? Imported beverages stay.")) return;
    try {
      await menuDeleteBatch(id);
      if (activeBatch?.id === id) setActiveBatch(null);
      await loadBatches();
    } catch (e) {
      setError(e.response?.data?.detail || "Delete failed");
    }
  };

  const onImport = async (actions) => {
    if (!activeBatch) return;
    setBusy(true); setError(null);
    try {
      const r = await menuImport(activeBatch.id, actions);
      await openBatch(activeBatch.id);
      await loadBatches();
      window.alert(
        `Import complete:\n${r.summary.created} created, ${r.summary.updated} updated, ${r.summary.skipped} skipped, ${r.summary.errored} errored.`
      );
    } catch (e) {
      setError(e.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="px-5 md:px-10 py-8 md:py-12 max-w-7xl mx-auto" data-testid="admin-menu-upload">
      <header className="mb-8">
        <p className="overline text-[#C17F59] mb-2">Menu Engine</p>
        <h1 className="font-display text-4xl text-white">Upload & Ingest</h1>
        <p className="text-white/55 mt-2 max-w-2xl text-sm leading-relaxed">
          Drop a CSV, XLSX, PDF, or menu image. BENO extracts text (or runs Tesseract OCR for scanned files), heuristically detects category, brand, expression, price, ABV, and country — then suggests Create / Merge / Skip per row. Nothing imports without your sign-off.
        </p>
      </header>

      {error && (
        <div className="mb-6 border border-red-500/40 bg-red-500/10 text-red-300 px-4 py-3 text-sm" data-testid="upload-error">
          {error}
        </div>
      )}

      {/* Upload tile */}
      <section className="bg-[#15171A] border border-white/8 p-6 mb-6">
        {caps && caps.notes && caps.notes.length > 0 && (
          <div className="mb-4 border border-amber-500/30 bg-amber-500/5 text-amber-300 px-3 py-2 text-xs flex items-start gap-2" data-testid="caps-warning">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <div>
              <p className="overline text-amber-300 mb-1">Reduced upload capabilities on this server</p>
              {caps.notes.map((n, i) => <p key={i} className="leading-snug">{n}</p>)}
              <p className="text-amber-300/70 mt-1">CSV and XLSX uploads always work — use them as a reliable fallback.</p>
            </div>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-4 justify-between">
          <div>
            <p className="overline text-white/40 mb-1">New batch</p>
            <p className="text-white/80 text-sm" data-testid="caps-summary">
              {(() => {
                const supported = ["CSV", "XLSX"];
                if (!caps || caps.pdf_native) supported.push("PDF");
                else if (caps.pdf_scanned) supported.push("PDF (scanned)");
                if (!caps || caps.image_ocr) supported.push("PNG/JPG menu image");
                return `${supported.join(" · ")} — up to 24 MB.`;
              })()}
            </p>
            <p className="text-white/40 text-xs mt-1">CSV/XLSX: headers on row 1 (category, brand, expression / name, price, abv, country, region, notes, availability, ml). PDF & images run through table extraction → text fallback → Tesseract OCR. Always confidence-scored; admin reviews before import.</p>
          </div>
          <div className="flex gap-3">
            <input ref={inputRef} type="file"
                   accept={(() => {
                     const exts = [".csv", ".xlsx", ".xlsm"];
                     if (!caps || caps.pdf_native || caps.pdf_scanned) exts.push(".pdf");
                     if (!caps || caps.image_ocr) exts.push(".png", ".jpg", ".jpeg", ".webp");
                     return exts.join(",");
                   })()}
                   onChange={(e) => onUpload(e.target.files?.[0])} className="hidden"
                   data-testid="upload-input" />
            <button onClick={() => inputRef.current?.click()} disabled={busy}
                    className="inline-flex items-center gap-2 bg-[#C17F59] text-[#0E0F0C] px-5 py-3 text-xs uppercase tracking-[0.25em] hover:bg-[#D89A74] disabled:opacity-50"
                    data-testid="upload-button">
              <UploadCloud size={14} /> {busy ? "Uploading..." : "Upload menu"}
            </button>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Batch list */}
        <aside className="lg:col-span-3">
          <p className="overline text-white/40 mb-3">Recent batches</p>
          <div className="space-y-2" data-testid="batch-list">
            {batches.length === 0 && (
              <p className="text-xs text-white/40 italic">No batches yet.</p>
            )}
            {batches.map((b) => (
              <button key={b.id} onClick={() => openBatch(b.id)}
                      data-testid={`batch-${b.id}`}
                      className={`w-full text-left bg-[#15171A] border p-3 transition-colors ${
                        activeBatch?.id === b.id ? "border-[#C17F59]" : "border-white/8 hover:border-white/20"
                      }`}>
                <div className="flex items-center gap-2">
                  <FileSpreadsheet size={14} className="text-[#C17F59]" />
                  <p className="text-xs text-white/80 truncate flex-1">{b.filename}</p>
                </div>
                <div className="flex items-center justify-between mt-2 text-[10px] text-white/40 uppercase tracking-[0.2em]">
                  <span>{b.row_count} row{b.row_count === 1 ? "" : "s"}</span>
                  <span>{statusLabel(b.status)}</span>
                </div>
              </button>
            ))}
          </div>
        </aside>

        {/* Active batch panel */}
        <section className="lg:col-span-9">
          {!activeBatch ? (
            <div className="bg-[#15171A] border border-white/8 p-10 text-center text-white/40">
              Upload a menu or pick a batch to review.
            </div>
          ) : (
            <BatchPanel
              batch={activeBatch}
              busy={busy}
              onCanonicalize={onCanonicalize}
              onImport={onImport}
              onDelete={() => onDeleteBatch(activeBatch.id)}
              onReload={() => openBatch(activeBatch.id)}
            />
          )}
        </section>
      </div>
    </div>
  );
}

function statusLabel(s) {
  if (s === "parsed") return "PARSED";
  if (s === "canonicalized") return "READY";
  if (s === "imported") return "IMPORTED";
  return (s || "").toUpperCase();
}

function BatchPanel({ batch, busy, onCanonicalize, onImport, onDelete, onReload }) {
  // Local copy of per-row planned values so admin can edit before import
  const [rows, setRows] = useState([]);
  const [actions, setActions] = useState({}); // row_index -> action

  useEffect(() => {
    const cr = batch.canonical_rows || [];
    setRows(cr.map((r) => ({ ...r, edit_planned: { ...r.planned } })));
    const initial = {};
    for (const r of cr) initial[r.row_index] = r.suggested_action === "blocked" ? "skip" : r.suggested_action;
    setActions(initial);
  }, [batch]);

  const summary = batch.summary || {};

  const onPick = (idx, action) => setActions((a) => ({ ...a, [idx]: action }));
  const onEdit = (idx, field, value) => {
    setRows((rs) => rs.map((r) => r.row_index === idx ? { ...r, edit_planned: { ...r.edit_planned, [field]: value } } : r));
  };

  const importable = rows.filter((r) => actions[r.row_index] && actions[r.row_index] !== "skip");

  const submit = () => {
    if (!window.confirm(`Import ${importable.length} row${importable.length === 1 ? "" : "s"}?`)) return;
    const payload = rows.map((r) => ({
      row_index: r.row_index,
      action: actions[r.row_index] || "skip",
      edited_planned: r.edit_planned,
      merge_target_id: r.match?.id || null,
    }));
    onImport(payload);
  };

  return (
    <div className="space-y-5" data-testid="batch-panel">
      {/* Header */}
      <div className="bg-[#15171A] border border-white/8 p-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="overline text-white/40 mb-1">{statusLabel(batch.status)} · {batch.filename}</p>
            <h2 className="font-display text-2xl text-white">{batch.row_count} rows</h2>
            {batch.summary?.canonicalized_at && (
              <p className="text-[10px] text-white/40 uppercase tracking-[0.22em] mt-1">
                Canonicalized at {new Date(batch.summary.canonicalized_at).toLocaleString()}
              </p>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={onCanonicalize} disabled={busy}
                    className="inline-flex items-center gap-2 border border-white/15 text-white/80 hover:text-[#C17F59] hover:border-[#C17F59] px-4 py-2 text-[11px] uppercase tracking-[0.25em] disabled:opacity-40"
                    data-testid="canonicalize-button">
              <Sparkles size={12} /> {batch.status === "parsed" ? "Canonicalize" : "Re-canonicalize"}
            </button>
            <button onClick={onReload} className="inline-flex items-center gap-2 border border-white/10 text-white/55 hover:text-white px-3 py-2 text-[11px] uppercase tracking-[0.25em]" data-testid="reload-batch">
              <RotateCcw size={12} /> Reload
            </button>
            <button onClick={onDelete} className="inline-flex items-center gap-2 border border-red-500/30 text-red-400 hover:bg-red-500/10 px-3 py-2 text-[11px] uppercase tracking-[0.25em]" data-testid="delete-batch">
              <Trash2 size={12} /> Delete
            </button>
          </div>
        </div>

        {summary?.by_confidence && (
          <div className="grid grid-cols-3 gap-3 mt-5">
            <Stat label="High confidence" value={summary.by_confidence.HIGH || 0} tone="ok" />
            <Stat label="Medium" value={summary.by_confidence.MEDIUM || 0} tone="warn" />
            <Stat label="Low" value={summary.by_confidence.LOW || 0} tone="bad" />
          </div>
        )}

        {summary?.import && (
          <p className="text-[10px] text-white/45 uppercase tracking-[0.22em] mt-4">
            Last import: <span className="text-green-400">{summary.import.created} created</span> ·{" "}
            <span className="text-[#C17F59]">{summary.import.updated} updated</span> ·{" "}
            <span className="text-white/40">{summary.import.skipped} skipped</span>
            {summary.import.errored ? <> · <span className="text-red-400">{summary.import.errored} errors</span></> : null}
          </p>
        )}
      </div>

      {/* Review table */}
      {rows.length === 0 ? (
        <div className="bg-[#15171A] border border-white/8 p-8 text-center text-white/40">
          {batch.status === "parsed" ? "Click \"Canonicalize\" to detect categories & brands." : "No rows to review."}
        </div>
      ) : (
        <>
          <div className="bg-[#15171A] border border-white/8 overflow-x-auto" data-testid="import-review-table">
            <table className="w-full text-xs">
              <thead className="border-b border-white/10 text-white/55">
                <tr>
                  <th className="text-left p-3 overline">Row</th>
                  <th className="text-left p-3 overline">Detected</th>
                  <th className="text-left p-3 overline">Brand & expression</th>
                  <th className="text-left p-3 overline">Price · ABV · ml</th>
                  <th className="text-left p-3 overline">Origin</th>
                  <th className="text-left p-3 overline">Match</th>
                  <th className="text-left p-3 overline">Confidence</th>
                  <th className="text-left p-3 overline">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <ReviewRow key={r.row_index} row={r}
                             action={actions[r.row_index]}
                             onPick={(a) => onPick(r.row_index, a)}
                             onEdit={(f, v) => onEdit(r.row_index, f, v)} />
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-[#15171A] border border-white/8 p-5 flex items-center justify-between flex-wrap gap-3">
            <p className="text-xs text-white/55">
              <span className="text-white">{importable.length}</span> row{importable.length === 1 ? "" : "s"} ready to import ·{" "}
              <span className="text-white/40">{rows.length - importable.length} skipped</span>
            </p>
            <button onClick={submit} disabled={busy || importable.length === 0}
                    className="inline-flex items-center gap-2 bg-[#C17F59] text-[#0E0F0C] px-6 py-3 text-xs uppercase tracking-[0.25em] hover:bg-[#D89A74] disabled:opacity-40"
                    data-testid="import-button">
              <Check size={13} /> Import {importable.length} row{importable.length === 1 ? "" : "s"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, tone }) {
  const color = tone === "ok" ? "text-green-400" : tone === "warn" ? "text-yellow-400" : tone === "bad" ? "text-red-400" : "text-white";
  return (
    <div className="bg-[#101113] border border-white/8 p-3">
      <p className="overline text-white/40 mb-1">{label}</p>
      <p className={`font-display text-2xl ${color}`} data-testid={`stat-${label.replace(/\s/g,"-").toLowerCase()}`}>{value}</p>
    </div>
  );
}

function ReviewRow({ row, action, onPick, onEdit }) {
  const [open, setOpen] = useState(false);
  const p = row.edit_planned || row.planned;
  const status = p.confidence_state || "LOW";
  const importStatus = row.import_status;
  const isImported = ["created", "merged"].includes(importStatus);

  const statusColor = status === "HIGH" ? "text-green-400" : status === "MEDIUM" ? "text-yellow-400" : "text-red-400";

  return (
    <>
      <tr className={`border-b border-white/5 ${isImported ? "opacity-50" : ""}`} data-testid={`row-${row.row_index}`}>
        <td className="p-3 align-top text-white/45 font-mono">{row.row_index + 1}</td>
        <td className="p-3 align-top">
          <p className="text-white/85 capitalize">{p.category || <span className="text-red-400">missing</span>}</p>
          {p.subcategory && <p className="text-[10px] text-white/40 uppercase mt-1">{p.subcategory}</p>}
        </td>
        <td className="p-3 align-top">
          <p className="font-display text-base text-white">{p.brand || <span className="text-red-400 text-xs">brand missing</span>}</p>
          <p className="text-[11px] text-white/55 mt-1">{p.expression || "—"}</p>
        </td>
        <td className="p-3 align-top text-white/65">
          {p.pricing_mode === "BOTTLE_ONLY" ? (
            <>
              <p data-testid={`row-${row.row_index}-bottle-price`}>Btl ₹{p.price_by_bottle || "—"}</p>
              <p className="text-[10px] text-[#C17F59] uppercase tracking-[0.18em]">bottle only</p>
            </>
          ) : p.pricing_mode === "BOTH" ? (
            <>
              <p>₹{p.price_by_glass || "—"} <span className="text-white/40">/ btl ₹{p.price_by_bottle}</span></p>
              <p className="text-[10px] text-white/40 uppercase">{p.abv || "?"}% · {p.standard_pour_ml || 30}ml</p>
            </>
          ) : (
            <>
              <p>₹{p.price_by_glass || p.price || "—"}</p>
              <p className="text-[10px] text-white/40 uppercase">{p.abv || "?"}% · {p.standard_pour_ml || 30}ml</p>
            </>
          )}
        </td>
        <td className="p-3 align-top text-white/55 text-[11px]">{[p.country, p.region].filter(Boolean).join(" · ") || "—"}</td>
        <td className="p-3 align-top">
          {row.match ? (
            <div className="text-[11px]">
              <p className="text-[#C17F59]">{row.match.display_name}</p>
              <p className="text-white/40 uppercase tracking-[0.2em] text-[9px] mt-1">existing SKU</p>
            </div>
          ) : (
            <span className="text-white/30 text-[10px] uppercase">No match</span>
          )}
        </td>
        <td className={`p-3 align-top ${statusColor}`}>
          <p className="font-mono">{p.confidence_score}/100</p>
          <p className="text-[9px] uppercase tracking-[0.2em]">{status}</p>
          {p.warnings?.length > 0 && (
            <div className="mt-1 text-[10px] text-yellow-500/80 flex items-center gap-1">
              <AlertTriangle size={10} /> {p.warnings.length} warn
            </div>
          )}
        </td>
        <td className="p-3 align-top">
          {isImported ? (
            <div className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-[0.2em] text-green-400 flex items-center gap-1">
                <Check size={11} /> {importStatus}
              </span>
              {(row.beverage_id || row.merged_into) && (
                <div className="flex flex-col gap-0.5 mt-1">
                  <Link to={`/admin/beverages/${row.beverage_id || row.merged_into}`}
                        className="text-[9px] uppercase tracking-[0.2em] text-[#C17F59] hover:text-[#D89A74]"
                        data-testid={`row-${row.row_index}-edit-link`}>
                    Edit in Control Room →
                  </Link>
                  <a href={`/spirits/item/${row.beverage_id || row.merged_into}`} target="_blank" rel="noreferrer"
                     className="text-[9px] uppercase tracking-[0.2em] text-white/45 hover:text-white"
                     data-testid={`row-${row.row_index}-public-link`}>
                    View public →
                  </a>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              <select value={action || "skip"} onChange={(e) => onPick(e.target.value)}
                      data-testid={`action-${row.row_index}`}
                      disabled={!p.brand || !p.category}
                      className="bg-[#101113] border border-white/15 text-white text-[11px] py-1 px-2 focus:outline-none focus:border-[#C17F59]">
                <option value="skip" className="bg-[#15171A] text-white">Skip</option>
                <option value="create" disabled={!p.brand || !p.category} className="bg-[#15171A] text-white">Create</option>
                <option value="merge" disabled={!row.match} className="bg-[#15171A] text-white">Merge</option>
              </select>
              <button onClick={() => setOpen((v) => !v)} className="text-[10px] uppercase tracking-[0.2em] text-white/45 hover:text-[#C17F59] flex items-center gap-1" data-testid={`edit-row-${row.row_index}`}>
                <ChevronDown size={11} className={`transition-transform ${open ? "rotate-180" : ""}`} /> Edit
              </button>
            </div>
          )}
        </td>
      </tr>
      {open && (
        <tr className="bg-[#101113] border-b border-white/5" data-testid={`row-${row.row_index}-edit`}>
          <td colSpan={8} className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[11px]">
              <Field label="Category">
                <select value={p.category || ""} onChange={(e) => onEdit("category", e.target.value)}
                        className="bg-[#101113] border border-white/15 text-white py-1 px-2 w-full">
                  <option value="" className="bg-[#15171A] text-white">—</option>
                  {CATEGORIES.map((c) => <option key={c} value={c} className="bg-[#15171A] text-white">{c}</option>)}
                </select>
              </Field>
              <Field label="Brand"><Input value={p.brand || ""} onChange={(v) => onEdit("brand", v)} /></Field>
              <Field label="Expression"><Input value={p.expression || ""} onChange={(v) => onEdit("expression", v)} /></Field>
              <Field label="Pricing mode">
                <select value={p.pricing_mode || "POUR_ONLY"} onChange={(e) => onEdit("pricing_mode", e.target.value)}
                        data-testid={`edit-${row.row_index}-pricing-mode`}
                        className="bg-[#101113] border border-white/15 text-white py-1 px-2 w-full">
                  <option value="POUR_ONLY" className="bg-[#15171A] text-white">Pour only</option>
                  <option value="BOTTLE_ONLY" className="bg-[#15171A] text-white">Bottle only</option>
                  <option value="BOTH" className="bg-[#15171A] text-white">Both</option>
                </select>
              </Field>
              {p.pricing_mode !== "BOTTLE_ONLY" && (
                <Field label="Glass / pour price (₹)">
                  <Input type="number" value={p.price_by_glass || ""} onChange={(v) => onEdit("price_by_glass", v ? Number(v) : null)} />
                </Field>
              )}
              {p.pricing_mode !== "POUR_ONLY" && (
                <Field label="Bottle price (₹)">
                  <Input type="number" value={p.price_by_bottle || ""} onChange={(v) => onEdit("price_by_bottle", v ? Number(v) : null)} />
                </Field>
              )}
              <Field label="Standard pour (ml)">
                <select value={p.standard_pour_ml || 30}
                        onChange={(e) => onEdit("standard_pour_ml", Number(e.target.value))}
                        className="bg-[#101113] border border-white/15 text-white py-1 px-2 w-full">
                  {[30, 45, 60, 90, 150, 175].map((ml) => (
                    <option key={ml} value={ml} className="bg-[#15171A] text-white">{ml} ml</option>
                  ))}
                </select>
              </Field>
              <Field label="ABV %"><Input type="number" value={p.abv || ""} onChange={(v) => onEdit("abv", v ? Number(v) : null)} /></Field>
              <Field label="Country"><Input value={p.country || ""} onChange={(v) => onEdit("country", v || null)} /></Field>
              <Field label="Region"><Input value={p.region || ""} onChange={(v) => onEdit("region", v || null)} /></Field>
            </div>
            <div className="mt-3 text-[10px] text-white/40 uppercase tracking-[0.22em]">
              Raw row: {JSON.stringify(row.raw)}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="overline text-white/40 mb-1 block">{label}</span>
      {children}
    </label>
  );
}

function Input({ value, onChange, type = "text" }) {
  return (
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
           className="w-full bg-transparent border border-white/15 focus:border-[#C17F59] focus:outline-none text-white py-1.5 px-2" />
  );
}
